import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Col,
    Form,
    InputNumber,
    Row,
    Select,
    Slider,
    Switch,
    Input,
    Modal,
    Button,
    Collapse,
    Typography,
    Divider,
    Badge,
} from 'antd';
import * as THREE from 'three';
import { observer } from 'mobx-react-lite';
import QuickTurnPartStore from '../../../stores/QuickTurnPartStore';
import PortalLoading from 'components/PortalLoading';
import { InfoCircleTwoTone, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import DimensionConstrainedAlert from './DimensionConstrainedAlert';

interface Props {
    mode?: string;
    showNameInput?: boolean;
    showInterestIndicators?: boolean;
}

const PartSpecificationForm = (props: Props) => {
    const {
        material,
        outerDiameter,
        overallLength,
        hasThruHole,
        innerDiameter,
        hasFrontCountersink,
        frontCountersinkDepth,
        frontCountersinkAngle,
        hasFlange,
        flangeDiameter,
        flangeThickness,
        hasFrontCounterbore,
        frontCounterboreDiameter,
        frontCounterboreDepth,
        chamferWidth,
        comingSoonModalHeader,
        comingSoonModalVisible,
    } = QuickTurnPartStore;

    const [geometry, setGeometry] = useState<THREE.BufferGeometry>();
    const [overallLengthInputFocused, setOverallLengthInputFocused] = useState<boolean>(false);
    const [needDifferentMaterialButtonVisible, setNeedDifferentMaterialButtonVisible] = useState<boolean>(false);

    const [openPanels, setOpenPanels] = useState<string | string[]>(['basic-dimensions']);

    const [form] = Form.useForm();

    function degreesToRadians(degrees: number) {
        return degrees * (Math.PI / 180);
    }

    function radiansToDegrees(radians: number) {
        return radians * (180 / Math.PI);
    }

    const maxOverallLength = (od?: number): number => {
        let max = 0;

        if (hasThruHole) {
            if (innerDiameter < 0.375) {
                max = 1;
            } else if (innerDiameter >= 0.375 && innerDiameter < 0.5) {
                max = 1.5;
            } else if (innerDiameter >= 0.5 && innerDiameter < 0.625) {
                max = 2;
            } else {
                max = 3;
            }
        } else {
            if ((od ? od : outerDiameter) * 3 > 3) {
                max = 3;
            } else {
                max = (od ? od : outerDiameter) * 3;
            }
        }

        return Number(max.toFixed(3));
    };

    const minFrontCountersinkDepth = 0.015;
    const minFrontCounterSinkAngle = 60;
    const maxFrontCountersinkAngle = 120;

    const maxInnerDiameter = (od?: number): number => {
        let max = (od || outerDiameter) - 0.1;

        if (overallLength > 2 || hasFrontCounterbore) {
            max = (od || outerDiameter) - 0.2;

            if (frontCounterboreDepth > 1.9) {
                max = (od || outerDiameter) - 0.3;
            } else {
                max = (od || outerDiameter) - 0.2;
            }
        }

        if (hasFrontCountersink) {
            const countersinkWidth = Number(
                (Math.tan(degreesToRadians(frontCountersinkAngle / 2)) * frontCountersinkDepth).toFixed(3)
            );

            max = outerDiameter - 0.05 - countersinkWidth * 2;
        }

        return Number(max.toFixed(3));
    };

    const minInnerDiameter = 0.25;

    const minOuterDiameter = () => {
        let min = 0.25;

        if (hasThruHole) {
            if (overallLength > 2) {
                min = innerDiameter + 0.2;
            } else {
                min = innerDiameter + 0.1;
            }

            if (hasFrontCounterbore) {
                if (frontCounterboreDepth > 1.9) {
                    min = frontCounterboreDiameter + 0.2;
                } else {
                    min = frontCounterboreDiameter + 0.1;
                }
            } else if (hasFrontCountersink) {
                min = QuickTurnPartStore.frontCountersinkMajorDiameter + 0.05;
            }
        }

        return Number(min.toFixed(3));
    };

    const minFlangeDiameter = Number((outerDiameter + 0.1).toFixed(3));

    const maxFlangeDiameter = Number((2).toFixed(3));

    const maxOuterDiameter = () => {
        return hasFlange ? Number((maxFlangeDiameter - 0.1).toFixed(3)) : 2;
    };

    const minOverallLength = () => {
        let min = 0.1;

        if (hasFlange || hasFrontCounterbore) {
            min += 0.05;
        }

        if (hasFrontCountersink && frontCountersinkDepth > 0.05) {
            min = frontCountersinkDepth + 0.05;
        }

        return Number(min.toFixed(3));
    };

    const minFrontCounterboreDiameter = () => {
        const max = innerDiameter + 0.1;

        return Number(max.toFixed(3));
    };

    const maxFrontCounterboreDiameter = (od?: number) => {
        let max = (od || outerDiameter) - 0.1;

        if (frontCounterboreDepth > 1.9) {
            max -= 0.1;
        }

        return Number(max.toFixed(3));
    };

    const minfrontCounterboreDepth = () => {
        const min = 0.05;

        return Number(min.toFixed(3));
    };
    const maxfrontCounterboreDepth = () => {
        const max = overallLength - 0.1;

        return Number(max.toFixed(3));
    };

    const minFlangeThickness = Number((0.1).toFixed(3));

    const maxFlangeThickness = () => {
        const max = overallLength - 0.05;

        return Number(max.toFixed(3));
    };

    const minChamferWidth = 0.005;
    const maxChamferWidth = 0.015;

    function calcFrontCountersinkDiameter(angle: number, id?: number) {
        const tangent = Math.tan(degreesToRadians(angle / 2)) * frontCountersinkDepth;

        return tangent * 2 + (id ? id : innerDiameter);
    }

    interface ICalcFrontCountersinkDepthProps {
        frontCountersinkAngle?: number;
        majorDiameter: number;
        innerDiameter?: number;
    }

    function calcFrontCountersinkDepth(values: ICalcFrontCountersinkDepthProps) {
        const depth =
            Math.tan(degreesToRadians(90 - (values?.frontCountersinkAngle || frontCountersinkAngle) / 2)) *
            (values?.majorDiameter - (values?.innerDiameter || innerDiameter));

        return depth;
    }

    interface IMaxFrontCountersinkDepthProps {
        frontCountersinkAngle?: number;
        outerDiameter?: number;
        innerDiameter?: number;
        overallLength?: number;
    }

    const maxFrontCountersinkDepth = (values?: IMaxFrontCountersinkDepthProps) => {
        const minorDiameter = values?.innerDiameter || innerDiameter;

        const max =
            (Math.tan(degreesToRadians(90 - (values?.frontCountersinkAngle || frontCountersinkAngle) / 2)) *
                ((values?.outerDiameter || outerDiameter) - minorDiameter - 0.05)) /
            2;

        // If calculation returns a smaller value than the minimum front countersink depth,
        // return minFrontCountersinkDepth
        if (max < minFrontCountersinkDepth) {
            return Number(minFrontCountersinkDepth.toFixed(3));
        }

        const length = values?.overallLength || overallLength;

        if (max > length - 0.05) {
            return Number((length - 0.05).toFixed(3));
        }

        return Number(max.toFixed(3));
    };

    // function minFrontCounterSinkAngle() {
    //     const side1 = calcFrontCountersinkDiameter() - innerDiameter / 2;
    //     const side2 = overallLength - 0.05;

    //     const angle = Number(radiansToDegrees(Math.asin(side2 / side1)).toFixed(0));

    //     return angle;
    // }

    function onChangeHasThruHole(value: boolean) {
        QuickTurnPartStore.setHasThruHole(value);

        if (value === true) {
            if (outerDiameter < 0.35) {
                onChangeInnerDiameter(0.25);
            } else {
                onChangeInnerDiameter(outerDiameter / 2);
            }

            QuickTurnPartStore.setHasFrontCounterbore(false);
            QuickTurnPartStore.setHasFrontCountersink(false);
        }
    }

    function onChangeInnerDiameter(value: number) {
        let newOverallLength = overallLength;
        let newOuterDiameter = outerDiameter;
        let newFlangeDiameter = flangeDiameter;
        let newFrontCountersinkDepth = frontCountersinkDepth;
        let newFlangeThickness = flangeThickness;
        const newFrontCounterboreDiameter = frontCounterboreDiameter;

        const newFrontCountersinkDiameter = calcFrontCountersinkDiameter(frontCountersinkAngle, value);

        if (overallLength > 2) {
            if (value >= outerDiameter - 0.2) {
                if (value + 0.2 <= maxOuterDiameter()) {
                    newOuterDiameter = value + 0.2;
                }
            }
        } else {
            if (value >= outerDiameter - 0.1) {
                if (value + 0.1 <= maxOuterDiameter()) {
                    newOuterDiameter = value + 0.1;
                }
            }
        }

        if (value < 0.375 && overallLength > 1) {
            newOverallLength = 1;

            if (hasFlange && flangeThickness > 0.95) {
                newFlangeThickness = 0.95;
            }

            if (hasFrontCountersink) {
                if (frontCountersinkDepth > 0.95) {
                    newFrontCountersinkDepth = 0.95;
                }
            }
        } else if (value >= 0.375 && value < 0.5 && overallLength > 1.5) {
            newOverallLength = 1.5;

            if (hasFlange && flangeThickness > 1.45) {
                newFlangeThickness = 1.45;
            }

            if (hasFrontCountersink) {
                if (frontCountersinkDepth > 1.45) {
                    newFrontCountersinkDepth = 1.45;
                }
            }
        } else if (value >= 0.5 && value <= 0.625 && overallLength > 2) {
            newOverallLength = 2;

            if (hasFlange && flangeThickness > 1.45) {
                newFlangeThickness = 1.95;
            }

            if (hasFrontCountersink) {
                if (frontCountersinkDepth > 1.95) {
                    newFrontCountersinkDepth = 1.95;
                }
            }
        }

        if (hasFlange) {
            if (newOuterDiameter > flangeDiameter - 0.1) {
                newFlangeDiameter = newOuterDiameter + 0.1;
            }
        }

        onChangeOverallLength(newOverallLength);

        QuickTurnPartStore.setOuterDiameter(newOuterDiameter);
        QuickTurnPartStore.setFlangeDiameter(newFlangeDiameter);
        QuickTurnPartStore.setFrontCountersinkDepth(newFrontCountersinkDepth);
        QuickTurnPartStore.setFlangeThickness(newFlangeThickness);
        QuickTurnPartStore.setFrontCounterboreDiameter(newFrontCounterboreDiameter);

        // if (newFrontCountersinkDiameter > outerDiameter - 0.1) {
        //     QuickTurnPartStore.setFrontCountersinkDepth(
        //         maxFrontCountersinkDepth({
        //             innerDiameter: value,
        //             overallLength: newOverallLength,
        //         })
        //     );
        // }

        if (value >= minInnerDiameter && value <= maxInnerDiameter(newOuterDiameter)) {
            QuickTurnPartStore.setInnerDiameter(value);
        }
    }

    function onChangeHasFrontCounterbore(value: boolean) {
        QuickTurnPartStore.setHasFrontCounterbore(value);

        if (value === true) {
            // Disable Front Countersink
            QuickTurnPartStore.setHasFrontCountersink(false);

            let newOuterDiameter = outerDiameter;

            if (QuickTurnPartStore.hasThruHole) {
                if (outerDiameter - innerDiameter > 0.2) {
                    QuickTurnPartStore.setFrontCounterboreDiameter((outerDiameter + innerDiameter) / 2);
                } else {
                    if (outerDiameter < maxOuterDiameter() - 0.1) {
                        newOuterDiameter = outerDiameter + 0.1;

                        if (hasFlange && flangeDiameter < newOuterDiameter + 0.1) {
                            QuickTurnPartStore.setFlangeDiameter(newOuterDiameter + 0.1);
                        }

                        QuickTurnPartStore.setOuterDiameter(newOuterDiameter);
                        QuickTurnPartStore.setFrontCounterboreDiameter(innerDiameter + 0.1);
                    } else {
                        QuickTurnPartStore.setInnerDiameter(outerDiameter - 0.2);
                        QuickTurnPartStore.setFrontCounterboreDiameter(outerDiameter - 0.1);
                    }
                }

                if (overallLength < 0.15) {
                    onChangeOverallLength(0.15);
                    QuickTurnPartStore.setFrontCounterboreDepth(0.05);
                } else {
                    QuickTurnPartStore.setFrontCounterboreDepth(overallLength / 3);
                }
            }
        }
    }

    function onChangeFrontCounterboreDiameter(value: number) {
        if (value >= minFrontCounterboreDiameter() && value <= maxFrontCounterboreDiameter()) {
            QuickTurnPartStore.setFrontCounterboreDiameter(value);
        }
    }

    function onChangeFrontCounterboreDepth(value: number) {
        if (value >= minfrontCounterboreDepth() && value <= maxfrontCounterboreDepth()) {
            QuickTurnPartStore.setFrontCounterboreDepth(value);

            let newInnerDiameter = innerDiameter;
            let newFrontCounterboreDiameter = frontCounterboreDiameter;

            // if (frontCounterboreDiameter > maxFrontCounterboreDiameter()) {
            //     QuickTurnPartStore.setFrontCounterboreDiameter(maxFrontCounterboreDiameter());
            // }

            if (value > 1.9) {
                if (outerDiameter - frontCounterboreDiameter < 0.2) {
                    if (innerDiameter >= maxInnerDiameter() - 0.1) {
                        newFrontCounterboreDiameter = outerDiameter - 0.2;
                        newInnerDiameter = newFrontCounterboreDiameter - 0.1;

                        QuickTurnPartStore.setInnerDiameter(newInnerDiameter);
                        QuickTurnPartStore.setFrontCounterboreDiameter(newFrontCounterboreDiameter);
                    } else {
                        QuickTurnPartStore.setOuterDiameter(frontCounterboreDiameter + 0.1);
                    }
                }
            }
        }
    }

    function onChangeHasFlange(value: boolean) {
        QuickTurnPartStore.setHasFlange(value);

        if (value === true) {
            QuickTurnPartStore.setFlangeThickness(0.1);

            if (overallLength <= 0.15) {
                QuickTurnPartStore.setOverallLength(0.15);
            }

            if (outerDiameter > maxFlangeDiameter - 0.1) {
                const newOuterDiameter = maxFlangeDiameter - 0.1;

                QuickTurnPartStore.setFlangeDiameter(maxFlangeDiameter);
                onChangeOuterDiameter(newOuterDiameter);

                // if (innerDiameter > maxInnerDiameter(newOuterDiameter)) {
                //     QuickTurnPartStore.setInnerDiameter(maxInnerDiameter(newOuterDiameter));
                // }
            } else {
                QuickTurnPartStore.setFlangeDiameter(outerDiameter + 0.1);
            }
        }
    }

    function onChangeFlangeDiameter(value: number) {
        if (value >= minFlangeDiameter && value <= maxFlangeDiameter) {
            QuickTurnPartStore.setFlangeDiameter(value);
        }
    }

    function onChangeFlangeThickness(value: number) {
        if (value >= minFlangeThickness && value <= maxFlangeThickness()) {
            QuickTurnPartStore.setFlangeThickness(value);
        }
    }

    function onChangeOuterDiameter(value: number) {
        let newOverallLength = overallLength;

        if (maxOverallLength(value) < newOverallLength) {
            newOverallLength = maxOverallLength(value);
            onChangeOverallLength(newOverallLength);
        }

        if (value > flangeDiameter - 0.1) {
            QuickTurnPartStore.setFlangeDiameter(value + 0.1);
        }

        if (value >= minOuterDiameter() && value <= maxOuterDiameter()) {
            QuickTurnPartStore.setOuterDiameter(value);
        }
    }

    function onChangeOverallLength(value: number) {
        if (value >= minOverallLength() && value <= maxOverallLength()) {
            QuickTurnPartStore.setOverallLength(value);

            if (hasFlange && flangeThickness > value - 0.05) {
                QuickTurnPartStore.setFlangeThickness(value - 0.05);
            }

            if (value > 2 && innerDiameter > outerDiameter - 0.2) {
                QuickTurnPartStore.setInnerDiameter(outerDiameter - 0.2);
            }

            if (hasFrontCountersink && value < frontCountersinkDepth + 0.05) {
                QuickTurnPartStore.setFrontCountersinkDepth(minFrontCountersinkDepth);
            }

            if (hasFrontCounterbore) {
                if (frontCounterboreDepth > value - 0.1) {
                    QuickTurnPartStore.setFrontCounterboreDepth(value - 0.1);
                }

                if (value > 2 && innerDiameter > outerDiameter - 0.2) {
                    QuickTurnPartStore.setFrontCounterboreDiameter(outerDiameter - 0.2);
                    QuickTurnPartStore.setInnerDiameter(outerDiameter - 0.3);
                }
            }
        }
    }

    function onChangeChamferWidth(value: number) {
        QuickTurnPartStore.setChamferWidth(value);
    }

    function onChangeHasFrontCountersink(value: boolean) {
        QuickTurnPartStore.setHasFrontCountersink(value);

        const depth =
            maxFrontCountersinkDepth() >= minFrontCountersinkDepth * 2
                ? maxFrontCountersinkDepth() / 2
                : maxFrontCountersinkDepth();

        QuickTurnPartStore.setFrontCountersinkDepth(depth);

        if (value === true) {
            // Disable counterbore
            QuickTurnPartStore.setHasFrontCounterbore(false);
        }
    }

    function onChangeFrontCountersinkAngle(value: number) {
        QuickTurnPartStore.setFrontCountersinkAngle(value);

        const newFrontCountersinkDepth = calcFrontCountersinkDepth({
            frontCountersinkAngle: value,
            majorDiameter: QuickTurnPartStore.frontCountersinkMajorDiameter,
        });

        if (calcFrontCountersinkDiameter(value) > outerDiameter - 0.1) {
            QuickTurnPartStore.setFrontCountersinkDepth(maxFrontCountersinkDepth({ frontCountersinkAngle: value }));

            if (newFrontCountersinkDepth > overallLength - 0.05) {
                QuickTurnPartStore.setOverallLength(newFrontCountersinkDepth - 0.05);
            }
        }
    }

    function onChangeFrontCountersinkDepth(value: number) {
        QuickTurnPartStore.setFrontCountersinkDepth(value);

        if (value > overallLength - 0.05) {
            QuickTurnPartStore.setOverallLength(value + 0.05);
        }
    }

    const loader = new THREE.BufferGeometryLoader();

    useEffect(() => {
        QuickTurnPartStore.getMaterials();
    }, []);

    function handleChange(): void {
        if (QuickTurnPartStore.partToken) {
            if (props?.mode === 'preview') {
                QuickTurnPartStore.previewPartById(QuickTurnPartStore.partToken);
            } else {
                QuickTurnPartStore.updatePartById(QuickTurnPartStore.partToken);
            }
        }
    }

    useEffect(() => {
        if (QuickTurnPartStore.bufferGeometry) {
            const bufferGeo = loader.parse(JSON.parse(QuickTurnPartStore.bufferGeometry));
            setGeometry(bufferGeo);
        }
    }, [QuickTurnPartStore.bufferGeometry]);

    return (
        <>
            {!QuickTurnPartStore.partToken || !QuickTurnPartStore.bufferGeometry ? (
                <PortalLoading />
            ) : (
                <>
                    <Form
                        layout="vertical"
                        className="quick-turn-spec-form"
                        requiredMark={false}
                        form={form}
                        scrollToFirstError={false}
                        id="quickturn-part-spec-form"
                    >
                        <div className="pb-0 px-4 pt-2">
                            <Row gutter={[0, 0]} className="m-0">
                                <Col span={24}>
                                    {props?.showNameInput ? (
                                        <Form.Item
                                            name="name"
                                            label="Name"
                                            className="flex-grow-1 m-0"
                                            initialValue={QuickTurnPartStore.name}
                                            rules={[{ required: true, message: 'Please enter a part name' }]}
                                        >
                                            <Input
                                                onChange={(e: any) => {
                                                    QuickTurnPartStore.setName(e.target.value);
                                                }}
                                            />
                                        </Form.Item>
                                    ) : null}
                                </Col>
                                <Col span={24}>
                                    <Form.Item label="Material" className="mb-0">
                                        <Select
                                            options={QuickTurnPartStore.materials?.map(x => {
                                                return {
                                                    label: `${x.type} ${x.alloy}`,
                                                    value: x.id,
                                                };
                                            })}
                                            value={material}
                                            onClick={() => setNeedDifferentMaterialButtonVisible(true)}
                                            onChange={(value: string) => {
                                                QuickTurnPartStore.setMaterial(value);
                                                handleChange();
                                            }}
                                        />
                                        {props?.showInterestIndicators !== false ? (
                                            <MissingFeatureLink
                                                buttonId="quickturn-need-different-material"
                                                value="Need a Different Material?"
                                                modalHeader="More Material Options are Coming Soon!"
                                                visible={needDifferentMaterialButtonVisible}
                                                source="Different Material Popup"
                                            />
                                        ) : null}
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                        <Collapse bordered={false} activeKey={openPanels} onChange={values => setOpenPanels(values)}>
                            <Collapse.Panel header="Basic Dimensions" key="basic-dimensions">
                                <Row gutter={[0, 0]} className="m-0">
                                    <Col span={24}>
                                        <Form.Item label="Outer Diameter (in.)" className="mb-1">
                                            <div
                                                className="d-flex flex-row align-items-center"
                                                style={{ columnGap: '20px' }}
                                            >
                                                {minOuterDiameter() === maxOuterDiameter() ? (
                                                    <>
                                                        <DimensionConstrainedAlert message="To change the Outer Diameter, please decrease the Inner Diameter." />
                                                    </>
                                                ) : (
                                                    <div className="d-flex flex-column flex-grow-1">
                                                        <Slider
                                                            className="w-100 m-0 my-2"
                                                            min={minOuterDiameter()}
                                                            max={maxOuterDiameter()}
                                                            step={0.001}
                                                            tooltip={{
                                                                formatter: (value?: number) => `${value?.toFixed(3)}`,
                                                            }}
                                                            onChange={(value: number) => {
                                                                if (value) {
                                                                    onChangeOuterDiameter(value);
                                                                }
                                                            }}
                                                            onAfterChange={(value: number) => {
                                                                if (value) {
                                                                    onChangeOuterDiameter(value);
                                                                    handleChange();
                                                                }
                                                            }}
                                                            value={outerDiameter}
                                                        />
                                                        <MinAndMaxDimensions
                                                            min={minOuterDiameter()}
                                                            max={maxOuterDiameter()}
                                                            unit="inch"
                                                        />
                                                    </div>
                                                )}
                                                <InputNumber
                                                    min={minOuterDiameter()}
                                                    max={maxOuterDiameter()}
                                                    value={outerDiameter}
                                                    precision={3}
                                                    step={0.001}
                                                    onChange={value => {
                                                        if (value) {
                                                            onChangeOuterDiameter(value);
                                                            handleChange();
                                                        }
                                                    }}
                                                    className="LoNotSensitive"
                                                    style={{ width: '100px' }}
                                                    disabled={minOuterDiameter() === maxOuterDiameter()}
                                                />
                                            </div>

                                            {props?.showInterestIndicators !== false ? (
                                                <MissingFeatureLink
                                                    buttonId="quickturn-need-larger-diameter"
                                                    modalHeader="Larger Diameters are Coming Soon!"
                                                    value="Need a Larger Diameter?"
                                                    visible={outerDiameter === maxOuterDiameter()}
                                                    source="Need Larger Diameter Popup"
                                                />
                                            ) : null}
                                        </Form.Item>
                                        <Form.Item label="Overall Length (in.)" className="mb-0">
                                            <div
                                                className="d-flex flex-row align-items-center"
                                                style={{ columnGap: '20px' }}
                                            >
                                                <div className="d-flex flex-column flex-grow-1">
                                                    <Slider
                                                        className="w-100 m-0 my-2"
                                                        min={minOverallLength()}
                                                        max={maxOverallLength()}
                                                        step={0.001}
                                                        tooltip={{
                                                            formatter: (value?: number) => `${value?.toFixed(3)}`,
                                                        }}
                                                        onChange={(value: number) => {
                                                            onChangeOverallLength(value);
                                                        }}
                                                        onAfterChange={(value: number) => {
                                                            onChangeOverallLength(value);
                                                            handleChange();
                                                        }}
                                                        value={overallLength}
                                                    />
                                                    <MinAndMaxDimensions
                                                        min={minOverallLength()}
                                                        max={maxOverallLength()}
                                                        unit="inch"
                                                    />
                                                </div>
                                                <InputNumber
                                                    min={minOverallLength()}
                                                    max={maxOverallLength()}
                                                    value={overallLength}
                                                    precision={3}
                                                    step={0.001}
                                                    // onBlur={() => setOverallLengthInputFocused(false)}
                                                    // onFocus={() => setOverallLengthInputFocused(true)}
                                                    onChange={value => {
                                                        if (value) {
                                                            onChangeOverallLength(value);
                                                            handleChange();
                                                        }
                                                    }}
                                                    style={{ width: '100px' }}
                                                    className="LoNotSensitive"
                                                />
                                            </div>
                                            {props?.showInterestIndicators !== false ? (
                                                <MissingFeatureLink
                                                    buttonId="quickturn-need-longer-overall-length"
                                                    value="Need a Longer Overall Length?"
                                                    modalHeader="Longer Overall Lengths are Coming Soon!"
                                                    visible={overallLength === maxOverallLength()}
                                                    source="Need Longer Overall Length Popup"
                                                />
                                            ) : null}
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Collapse.Panel>

                            <Collapse.Panel
                                key="flange"
                                header="Flange"
                                extra={[
                                    <div key="1" onClick={event => event.stopPropagation()}>
                                        <div className="d-flex align-items-center" style={{ columnGap: '6px' }}>
                                            <Switch
                                                size="small"
                                                checked={hasFlange}
                                                onChange={(value: boolean) => {
                                                    if (
                                                        Array.isArray(openPanels) &&
                                                        !openPanels.find(x => x === 'flange') &&
                                                        value === true
                                                    ) {
                                                        setOpenPanels(openPanels.concat('flange'));
                                                    }

                                                    onChangeHasFlange(value);
                                                    handleChange();
                                                }}
                                            />
                                            <span className="font-size-base font-weight-normal">
                                                {hasFlange ? 'On' : 'Off'}
                                            </span>
                                        </div>
                                    </div>,
                                ]}
                            >
                                {hasFlange ? (
                                    <>
                                        <Row gutter={[0, 0]} className="m-0">
                                            <Col span={24}>
                                                <Form.Item label="Flange Diameter (in.)">
                                                    <div
                                                        className="d-flex flex-row align-items-center"
                                                        style={{ columnGap: '20px' }}
                                                    >
                                                        {minFlangeDiameter === maxFlangeDiameter ? (
                                                            <>
                                                                <DimensionConstrainedAlert message="To change the Flange Diameter, please decrease the Outer Diameter." />
                                                            </>
                                                        ) : (
                                                            <div className="d-flex flex-column flex-grow-1">
                                                                <Slider
                                                                    className="w-100 m-0 my-2"
                                                                    min={minFlangeDiameter}
                                                                    max={maxFlangeDiameter}
                                                                    value={flangeDiameter}
                                                                    step={0.001}
                                                                    tooltip={{
                                                                        formatter: (value?: number) =>
                                                                            `${value?.toFixed(3)}`,
                                                                    }}
                                                                    onChange={(value: number) => {
                                                                        onChangeFlangeDiameter(value);
                                                                    }}
                                                                    onAfterChange={(value: number) => {
                                                                        onChangeFlangeDiameter(value);
                                                                        handleChange();
                                                                    }}
                                                                />
                                                                <MinAndMaxDimensions
                                                                    min={minFlangeDiameter}
                                                                    max={maxFlangeDiameter}
                                                                    unit="inch"
                                                                />
                                                            </div>
                                                        )}
                                                        <InputNumber
                                                            min={minFlangeDiameter}
                                                            max={maxFlangeDiameter}
                                                            value={flangeDiameter}
                                                            precision={3}
                                                            step={0.001}
                                                            onChange={value => {
                                                                if (value) {
                                                                    onChangeFlangeDiameter(value);
                                                                    handleChange();
                                                                }
                                                            }}
                                                            style={{ width: '100px' }}
                                                            className="LoNotSensitive"
                                                        />
                                                    </div>
                                                </Form.Item>
                                                <Form.Item label="Flange Length (in.)">
                                                    <div
                                                        className="d-flex flex-row align-items-center"
                                                        style={{ columnGap: '20px' }}
                                                    >
                                                        {minFlangeThickness === maxFlangeThickness() ? (
                                                            <>
                                                                <DimensionConstrainedAlert message="To change the Flange Length, please increase the Overall Length." />
                                                            </>
                                                        ) : (
                                                            <div className="d-flex flex-column flex-grow-1">
                                                                <Slider
                                                                    className="w-100 m-0 my-2"
                                                                    min={minFlangeThickness}
                                                                    max={maxFlangeThickness()}
                                                                    value={flangeThickness}
                                                                    step={0.001}
                                                                    tooltip={{
                                                                        formatter: (value?: number) =>
                                                                            `${value?.toFixed(3)}`,
                                                                    }}
                                                                    onChange={(value: number) => {
                                                                        onChangeFlangeThickness(value);
                                                                    }}
                                                                    onAfterChange={(value: number) => {
                                                                        onChangeFlangeThickness(value);
                                                                        handleChange();
                                                                    }}
                                                                />
                                                                <MinAndMaxDimensions
                                                                    min={minFlangeThickness}
                                                                    max={maxFlangeThickness()}
                                                                    unit="inch"
                                                                />
                                                            </div>
                                                        )}
                                                        <InputNumber
                                                            min={minFlangeThickness}
                                                            max={maxFlangeThickness()}
                                                            value={flangeThickness}
                                                            precision={3}
                                                            step={0.001}
                                                            onChange={value => {
                                                                if (value) {
                                                                    onChangeFlangeThickness(value);
                                                                    handleChange();
                                                                }
                                                            }}
                                                            style={{ width: '100px' }}
                                                            className="LoNotSensitive"
                                                        />
                                                    </div>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </>
                                ) : null}
                            </Collapse.Panel>
                            <Collapse.Panel
                                key="thru-hole"
                                header="Thru-Hole"
                                extra={[
                                    <div key="1" onClick={event => event.stopPropagation()}>
                                        <div className="d-flex align-items-center" style={{ columnGap: '6px' }}>
                                            <Switch
                                                size="small"
                                                checked={hasThruHole}
                                                onChange={(value: boolean) => {
                                                    if (
                                                        Array.isArray(openPanels) &&
                                                        !openPanels.find(x => x === 'thru-hole') &&
                                                        value === true
                                                    ) {
                                                        setOpenPanels(openPanels.concat('thru-hole'));
                                                    }

                                                    onChangeHasThruHole(value);
                                                    handleChange();
                                                }}
                                            />
                                            <span className="font-size-base font-weight-normal">
                                                {hasThruHole ? 'On' : 'Off'}
                                            </span>
                                        </div>
                                    </div>,
                                ]}
                            >
                                <Row gutter={[0, 0]} className="m-0">
                                    <Col span={24}>
                                        {hasThruHole ? (
                                            <Form.Item label="Thru-Hole Diameter (in.)">
                                                <div
                                                    className="d-flex flex-row align-items-center"
                                                    style={{ columnGap: '20px' }}
                                                >
                                                    <div className="d-flex flex-column flex-grow-1">
                                                        <Slider
                                                            className="w-100 m-0 my-2"
                                                            min={minInnerDiameter}
                                                            max={maxInnerDiameter()}
                                                            value={innerDiameter}
                                                            step={0.001}
                                                            tooltip={{
                                                                formatter: (value?: number) => `${value?.toFixed(3)}`,
                                                            }}
                                                            onChange={(value: number) => {
                                                                onChangeInnerDiameter(value);
                                                            }}
                                                            onAfterChange={(value: number) => {
                                                                onChangeInnerDiameter(value);

                                                                handleChange();
                                                            }}
                                                        />
                                                        <MinAndMaxDimensions
                                                            min={minInnerDiameter}
                                                            max={maxInnerDiameter()}
                                                            unit="inch"
                                                        />
                                                    </div>
                                                    <InputNumber
                                                        min={minInnerDiameter}
                                                        max={maxInnerDiameter()}
                                                        value={innerDiameter}
                                                        precision={3}
                                                        step={0.001}
                                                        onChange={value => {
                                                            if (value) {
                                                                onChangeInnerDiameter(value);
                                                                handleChange();
                                                            }
                                                        }}
                                                        style={{ width: '100px' }}
                                                        className="LoNotSensitive"
                                                    />
                                                </div>
                                            </Form.Item>
                                        ) : null}
                                    </Col>
                                </Row>
                            </Collapse.Panel>
                            {hasThruHole ? (
                                <>
                                    <Collapse.Panel
                                        key="front-counterbore"
                                        header={
                                            <>
                                                <Badge
                                                    count="NEW"
                                                    color="#cd2027"
                                                    style={{
                                                        height: '0.9rem',
                                                        fontSize: '11px',
                                                        fontWeight: 500,
                                                        lineHeight: '0.9rem',
                                                    }}
                                                >
                                                    <span style={{ fontSize: '17px', color: '#232427' }}>
                                                        Counterbore
                                                    </span>
                                                </Badge>
                                            </>
                                        }
                                        extra={[
                                            <div key="1" onClick={event => event.stopPropagation()}>
                                                <div className="d-flex align-items-center" style={{ columnGap: '6px' }}>
                                                    <Switch
                                                        size="small"
                                                        checked={hasFrontCounterbore}
                                                        onChange={(value: boolean) => {
                                                            if (
                                                                Array.isArray(openPanels) &&
                                                                !openPanels.find(x => x === 'front-counterbore') &&
                                                                value === true
                                                            ) {
                                                                setOpenPanels(openPanels.concat('front-counterbore'));
                                                            }

                                                            onChangeHasFrontCounterbore(value);
                                                            handleChange();
                                                        }}
                                                    />
                                                    <span className="font-size-base font-weight-normal">
                                                        {hasFrontCounterbore ? 'On' : 'Off'}
                                                    </span>
                                                </div>
                                            </div>,
                                        ]}
                                    >
                                        {hasFrontCounterbore ? (
                                            <>
                                                <Form.Item label="Counterbore Diameter (in.)">
                                                    <div
                                                        className="d-flex flex-row align-items-center"
                                                        style={{ columnGap: '20px' }}
                                                    >
                                                        {minFrontCounterboreDiameter() ===
                                                        maxFrontCounterboreDiameter() ? (
                                                            <>
                                                                <DimensionConstrainedAlert message="To change the Counterbore Diameter, please increase the Outer Diameter or decrease the Inner Diameter." />
                                                            </>
                                                        ) : (
                                                            <div className="d-flex flex-column flex-grow-1">
                                                                <Slider
                                                                    className="w-100 m-0 my-2"
                                                                    min={minFrontCounterboreDiameter()}
                                                                    max={maxFrontCounterboreDiameter()}
                                                                    value={frontCounterboreDiameter}
                                                                    step={0.001}
                                                                    tooltip={{
                                                                        formatter: (value?: number) =>
                                                                            `${value?.toFixed(3)}`,
                                                                    }}
                                                                    onChange={(value: number) => {
                                                                        onChangeFrontCounterboreDiameter(value);
                                                                    }}
                                                                    onAfterChange={(value: number) => {
                                                                        onChangeFrontCounterboreDiameter(value);
                                                                        handleChange();
                                                                    }}
                                                                    disabled={
                                                                        minFrontCounterboreDiameter() ===
                                                                        maxFrontCounterboreDiameter()
                                                                    }
                                                                />
                                                                <MinAndMaxDimensions
                                                                    min={minFrontCounterboreDiameter()}
                                                                    max={maxFrontCounterboreDiameter()}
                                                                    unit="inch"
                                                                />
                                                            </div>
                                                        )}
                                                        <InputNumber
                                                            min={minFrontCounterboreDiameter()}
                                                            max={maxFrontCounterboreDiameter()}
                                                            value={frontCounterboreDiameter}
                                                            precision={3}
                                                            step={0.001}
                                                            onChange={value => {
                                                                if (value) {
                                                                    onChangeFrontCounterboreDiameter(value);
                                                                    handleChange();
                                                                }
                                                            }}
                                                            disabled={
                                                                minFrontCounterboreDiameter() ===
                                                                maxFrontCounterboreDiameter()
                                                            }
                                                            style={{ width: '100px' }}
                                                            className="LoNotSensitive"
                                                        />
                                                    </div>
                                                </Form.Item>
                                                <Form.Item label="Counterbore Depth">
                                                    <div
                                                        className="d-flex flex-row align-items-center"
                                                        style={{ columnGap: '20px' }}
                                                    >
                                                        {minfrontCounterboreDepth() === maxfrontCounterboreDepth() ? (
                                                            <>
                                                                <DimensionConstrainedAlert message="To change the Counterbore Depth, please increase the Overall Length." />
                                                            </>
                                                        ) : (
                                                            <div className="d-flex flex-column flex-grow-1">
                                                                <Slider
                                                                    className="w-100 m-0 my-2"
                                                                    min={minfrontCounterboreDepth()}
                                                                    max={maxfrontCounterboreDepth()}
                                                                    value={frontCounterboreDepth}
                                                                    step={0.001}
                                                                    tooltip={{
                                                                        formatter: (value?: number) =>
                                                                            `${value?.toFixed(3)}`,
                                                                    }}
                                                                    onChange={(value: number) => {
                                                                        onChangeFrontCounterboreDepth(value);
                                                                    }}
                                                                    onAfterChange={(value: number) => {
                                                                        onChangeFrontCounterboreDepth(value);
                                                                        handleChange();
                                                                    }}
                                                                    disabled={
                                                                        minfrontCounterboreDepth() ===
                                                                        maxfrontCounterboreDepth()
                                                                    }
                                                                />
                                                                <MinAndMaxDimensions
                                                                    min={minfrontCounterboreDepth()}
                                                                    max={maxfrontCounterboreDepth()}
                                                                    unit="inch"
                                                                />
                                                            </div>
                                                        )}
                                                        <InputNumber
                                                            min={minfrontCounterboreDepth()}
                                                            max={maxfrontCounterboreDepth()}
                                                            value={frontCounterboreDepth}
                                                            precision={3}
                                                            step={0.001}
                                                            onChange={value => {
                                                                if (value) {
                                                                    onChangeFrontCounterboreDepth(value);
                                                                    handleChange();
                                                                }
                                                            }}
                                                            disabled={
                                                                minfrontCounterboreDepth() ===
                                                                maxfrontCounterboreDepth()
                                                            }
                                                            style={{ width: '100px' }}
                                                            className="LoNotSensitive"
                                                        />
                                                    </div>
                                                </Form.Item>
                                            </>
                                        ) : null}
                                    </Collapse.Panel>
                                    <Collapse.Panel
                                        key="front-countersink"
                                        header="Countersink"
                                        extra={[
                                            <div key="1" onClick={event => event.stopPropagation()}>
                                                <div className="d-flex align-items-center" style={{ columnGap: '6px' }}>
                                                    <Switch
                                                        size="small"
                                                        checked={hasFrontCountersink}
                                                        onChange={(value: boolean) => {
                                                            if (
                                                                Array.isArray(openPanels) &&
                                                                !openPanels.find(x => x === 'front-countersink') &&
                                                                value === true
                                                            ) {
                                                                setOpenPanels(openPanels.concat('front-countersink'));
                                                            }
                                                            onChangeHasFrontCountersink(value);
                                                            handleChange();
                                                        }}
                                                    />
                                                    <span className="font-size-base font-weight-normal">
                                                        {hasFrontCountersink ? 'On' : 'Off'}
                                                    </span>
                                                </div>
                                            </div>,
                                        ]}
                                    >
                                        <Row gutter={[0, 0]} className="m-0">
                                            <Col span={24}>
                                                {hasFrontCountersink ? (
                                                    <>
                                                        <Form.Item label="Countersink Angle (deg.)">
                                                            <div
                                                                className="d-flex flex-row align-items-center"
                                                                style={{ columnGap: '20px' }}
                                                            >
                                                                <div className="d-flex flex-column flex-grow-1">
                                                                    <Slider
                                                                        className="w-100 m-0 my-2"
                                                                        min={minFrontCounterSinkAngle}
                                                                        max={maxFrontCountersinkAngle}
                                                                        value={frontCountersinkAngle}
                                                                        step={1}
                                                                        tooltip={{
                                                                            formatter: (value?: number) =>
                                                                                `${value?.toFixed(1)}°`,
                                                                        }}
                                                                        onChange={(value: number) => {
                                                                            onChangeFrontCountersinkAngle(value);
                                                                        }}
                                                                        onAfterChange={(value: number) => {
                                                                            onChangeFrontCountersinkAngle(value);
                                                                            handleChange();
                                                                        }}
                                                                    />
                                                                    <MinAndMaxDimensions
                                                                        min={minFrontCounterSinkAngle}
                                                                        max={maxFrontCountersinkAngle}
                                                                        unit="angle"
                                                                    />
                                                                </div>
                                                                <InputNumber
                                                                    min={minFrontCounterSinkAngle}
                                                                    max={maxFrontCountersinkAngle}
                                                                    value={frontCountersinkAngle}
                                                                    precision={0}
                                                                    step={1}
                                                                    onChange={value => {
                                                                        if (value) {
                                                                            onChangeFrontCountersinkAngle(value);
                                                                            handleChange();
                                                                        }
                                                                    }}
                                                                    style={{ width: '100px' }}
                                                                    className="LoNotSensitive"
                                                                />
                                                            </div>
                                                        </Form.Item>
                                                        <Form.Item label="Countersink Depth (in.)">
                                                            {frontCountersinkDepth === maxFrontCountersinkDepth() &&
                                                            frontCountersinkDepth === minFrontCountersinkDepth ? (
                                                                <DimensionConstrainedAlert message="To increase the Countersink Depth, please change the Thru-Hole Diameter or Outer Diameter." />
                                                            ) : (
                                                                <div
                                                                    className="d-flex flex-row align-items-center"
                                                                    style={{ columnGap: '20px' }}
                                                                >
                                                                    <div className="d-flex flex-column flex-grow-1">
                                                                        <Slider
                                                                            className="w-100 m-0 my-2"
                                                                            min={minFrontCountersinkDepth}
                                                                            max={maxFrontCountersinkDepth()}
                                                                            value={frontCountersinkDepth}
                                                                            step={0.001}
                                                                            tooltip={{
                                                                                formatter: (value?: number) =>
                                                                                    `${value?.toFixed(3)}`,
                                                                            }}
                                                                            onChange={(value: number) => {
                                                                                onChangeFrontCountersinkDepth(value);
                                                                            }}
                                                                            onAfterChange={(value: number) => {
                                                                                onChangeFrontCountersinkDepth(value);
                                                                                handleChange();
                                                                            }}
                                                                        />
                                                                        <MinAndMaxDimensions
                                                                            min={minFrontCountersinkDepth}
                                                                            max={maxFrontCountersinkDepth()}
                                                                            unit="inch"
                                                                        />
                                                                    </div>
                                                                    <InputNumber
                                                                        min={minFrontCountersinkDepth}
                                                                        max={maxFrontCountersinkDepth()}
                                                                        value={frontCountersinkDepth}
                                                                        precision={3}
                                                                        step={0.001}
                                                                        onChange={value => {
                                                                            if (value) {
                                                                                onChangeFrontCountersinkDepth(value);
                                                                                handleChange();
                                                                            }
                                                                        }}
                                                                        style={{ width: '100px' }}
                                                                        className="LoNotSensitive"
                                                                    />
                                                                </div>
                                                            )}
                                                        </Form.Item>
                                                    </>
                                                ) : null}
                                            </Col>
                                        </Row>
                                    </Collapse.Panel>
                                </>
                            ) : null}
                            <Collapse.Panel header="Chamfer" key="chamfer">
                                <Row gutter={[0, 0]} className="m-0">
                                    <Col span={24}>
                                        <Form.Item label="Chamfer Size">
                                            <div
                                                className="d-flex flex-row align-items-center"
                                                style={{ columnGap: '20px' }}
                                            >
                                                <div className="d-flex flex-column flex-grow-1">
                                                    <Slider
                                                        className="w-100 m-0 my-2"
                                                        min={minChamferWidth}
                                                        max={maxChamferWidth}
                                                        value={chamferWidth}
                                                        step={0.001}
                                                        tooltip={{
                                                            formatter: (value?: number) => `${value?.toFixed(3)}`,
                                                        }}
                                                        onChange={(value: number) => {
                                                            onChangeChamferWidth(value);
                                                        }}
                                                        onAfterChange={(value: number) => {
                                                            onChangeChamferWidth(value);

                                                            handleChange();
                                                        }}
                                                    />
                                                    <MinAndMaxDimensions
                                                        min={minChamferWidth}
                                                        max={maxChamferWidth}
                                                        unit="inch"
                                                    />
                                                </div>
                                                <InputNumber
                                                    min={minChamferWidth}
                                                    max={maxChamferWidth}
                                                    value={chamferWidth}
                                                    step={0.001}
                                                    onChange={value => {
                                                        if (value) {
                                                            onChangeChamferWidth(value);
                                                            handleChange();
                                                        }
                                                    }}
                                                    style={{ width: '100px' }}
                                                    className="LoNotSensitive"
                                                />
                                            </div>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Collapse.Panel>
                            {props?.showInterestIndicators !== false ? (
                                <>
                                    <Collapse.Panel
                                        key="outside-threads"
                                        header="Outside Threads"
                                        extra={[
                                            <div key="1" onClick={event => event.stopPropagation()}>
                                                <div className="d-flex align-items-center" style={{ columnGap: '6px' }}>
                                                    <Switch
                                                        id="quickturn-need-od-threads"
                                                        size="small"
                                                        checked={false}
                                                        onClick={() => {
                                                            QuickTurnPartStore.setComingSoonModalHeader(
                                                                'Outside Threads are Coming Soon!'
                                                            );
                                                            QuickTurnPartStore.setComingSoonModalVisible(true);
                                                            QuickTurnPartStore.setEmailSignUpSource(
                                                                'Need Outside Threads Popup'
                                                            );
                                                        }}
                                                    />
                                                    <span className="font-size-base font-weight-normal">Off</span>
                                                </div>
                                            </div>,
                                        ]}
                                    />
                                    <Collapse.Panel
                                        key="inside-threads"
                                        header="Inside Threads"
                                        extra={[
                                            <div key="1" onClick={event => event.stopPropagation()}>
                                                <div className="d-flex align-items-center" style={{ columnGap: '6px' }}>
                                                    <Switch
                                                        id="quickturn-need-id-threads"
                                                        size="small"
                                                        checked={false}
                                                        onClick={() => {
                                                            QuickTurnPartStore.setComingSoonModalHeader(
                                                                'Inside Threads are Coming Soon!'
                                                            );
                                                            QuickTurnPartStore.setComingSoonModalVisible(true);
                                                            QuickTurnPartStore.setEmailSignUpSource(
                                                                'Need Inside Threads Popup'
                                                            );
                                                        }}
                                                    />
                                                    <span className="font-size-base font-weight-normal">Off</span>
                                                </div>
                                            </div>,
                                        ]}
                                    />
                                    <Collapse.Panel
                                        key="outside-taper"
                                        header="Outside Taper"
                                        extra={[
                                            <div key="1" onClick={event => event.stopPropagation()}>
                                                <div className="d-flex align-items-center" style={{ columnGap: '6px' }}>
                                                    <Switch
                                                        id="quickturn-need-od-taper"
                                                        size="small"
                                                        checked={false}
                                                        onClick={() => {
                                                            QuickTurnPartStore.setComingSoonModalHeader(
                                                                'Outside Tapers are Coming Soon!'
                                                            );
                                                            QuickTurnPartStore.setComingSoonModalVisible(true);
                                                            QuickTurnPartStore.setEmailSignUpSource(
                                                                'Need Outside Tapers Popup'
                                                            );
                                                        }}
                                                    />
                                                    <span className="font-size-base font-weight-normal">Off</span>
                                                </div>
                                            </div>,
                                        ]}
                                    />
                                    <Collapse.Panel
                                        key="flats"
                                        header="Flats"
                                        extra={[
                                            <div key="1" onClick={event => event.stopPropagation()}>
                                                <div className="d-flex align-items-center" style={{ columnGap: '6px' }}>
                                                    <Switch
                                                        id="quickturn-need-flats"
                                                        size="small"
                                                        checked={false}
                                                        onClick={() => {
                                                            QuickTurnPartStore.setComingSoonModalHeader(
                                                                'Flats are Coming Soon!'
                                                            );
                                                            QuickTurnPartStore.setComingSoonModalVisible(true);
                                                            QuickTurnPartStore.setEmailSignUpSource('Need Flats Popup');
                                                        }}
                                                    />
                                                    <span className="font-size-base font-weight-normal">Off</span>
                                                </div>
                                            </div>,
                                        ]}
                                    />
                                </>
                            ) : null}
                        </Collapse>
                        {props?.showInterestIndicators !== false ? (
                            <>
                                <Divider className="m-0 p-0" />
                                <div className="p-3">
                                    <Row>
                                        <Col span={24}>
                                            <Col span={24}>
                                                <Button
                                                    id="quickturn-upload-solid-model"
                                                    type="default"
                                                    icon={<UploadOutlined />}
                                                    block
                                                    size="small"
                                                    className="mb-0"
                                                    onClick={() => {
                                                        QuickTurnPartStore.setUploadSolidModelModelVisible(true);
                                                        QuickTurnPartStore.setEmailSignUpSource(
                                                            'Need Solid Model Popup'
                                                        );
                                                    }}
                                                >
                                                    Have a Solid Model?
                                                </Button>
                                            </Col>
                                        </Col>
                                    </Row>
                                </div>
                            </>
                        ) : null}
                    </Form>
                </>
            )}

            <Modal
                open={QuickTurnPartStore.comingSoonModalVisible}
                footer={null}
                centered={true}
                width="640px"
                onCancel={() => QuickTurnPartStore.setComingSoonModalVisible(false)}
                destroyOnClose={true}
                zIndex={9999}
            >
                <h2 className="mb-4 plus-jakarta-sans font-weight-bold">{comingSoonModalHeader}</h2>

                <h4 className="font-weight-light">Sign up to get notified when new features are released!</h4>

                <EmailSignUpForm
                    showDismissButton={true}
                    onDismiss={() => QuickTurnPartStore.setComingSoonModalVisible(false)}
                />
            </Modal>

            <Modal
                open={QuickTurnPartStore.uploadSolidModelModelVisible}
                footer={null}
                centered={true}
                width="640px"
                onCancel={() => QuickTurnPartStore.setUploadSolidModelModelVisible(false)}
                destroyOnClose={true}
                zIndex={9999}
            >
                <h2 className="mb-3 plus-jakarta-sans font-weight-bold">Solid model uploads are not yet supported</h2>

                <div className="mb-1">Sign up to get notified when Solid Model uploads are supported!</div>

                <div className="mb-4">
                    <EmailSignUpForm
                        showDismissButton={false}
                        onDismiss={() => QuickTurnPartStore.setUploadSolidModelModelVisible(false)}
                        submitButtonType="default"
                    />
                </div>

                <div className="mb-0">
                    <Typography.Paragraph>
                        Or you may request a quote through our Rapid Quote system.
                    </Typography.Paragraph>
                </div>

                <Row gutter={[16, 16]} className="mb-0">
                    <Col md={{ order: 1, span: 12 }} xs={{ order: 2, span: 24 }}>
                        <Button
                            type="default"
                            block
                            onClick={() => QuickTurnPartStore.setUploadSolidModelModelVisible(false)}
                        >
                            Go Back
                        </Button>
                    </Col>
                    <Col md={{ order: 2, span: 12 }} xs={{ order: 1, span: 24 }}>
                        <Button
                            id="quickturn-request-rapid-quote"
                            type="primary"
                            htmlType="submit"
                            block
                            onClick={() => QuickTurnPartStore.setUploadSolidModelModelVisible(false)}
                        >
                            <Link to="/rapid-quote/start">Get a Rapid Quote</Link>
                        </Button>
                    </Col>
                </Row>
            </Modal>
        </>
    );
};

interface MissingFeatureLinkProps {
    visible: boolean;
    value: string;
    buttonId: string;
    modalHeader: string;
    source: string;
}

const MissingFeatureLink = (props: MissingFeatureLinkProps) => {
    return (
        <>
            <div
                className={
                    props?.visible ? 'opacity-1 d-flex justify-content-end' : 'opacity-0 d-flex justify-content-end'
                }
                style={{ transition: 'all 0.2s linear' }}
            >
                <Button
                    id={props?.buttonId}
                    type="link"
                    className="font-size-small m-0 p-0 h-auto mt-1"
                    onClick={() => {
                        QuickTurnPartStore.setComingSoonModalHeader(props?.modalHeader);
                        QuickTurnPartStore.setComingSoonModalVisible(true);
                        QuickTurnPartStore.setEmailSignUpSource(props?.source);
                    }}
                >
                    <span className="underline text-black-50">{props?.value}</span>
                </Button>
            </div>
        </>
    );
};

interface IEmailSignUpFormProps {
    showDismissButton: boolean;
    onDismiss: () => void;
    submitButtonType?: 'primary' | 'default';
}

const EmailSignUpForm = observer((props: IEmailSignUpFormProps) => {
    return QuickTurnPartStore.userSignedUpForEmailNotifications ? (
        <>
            <Alert
                type="success"
                showIcon={true}
                message={<span>You are signed up to receive email notifications</span>}
                className="mb-2"
            />
            {props?.showDismissButton ? (
                <Button type="default" htmlType="button" block onClick={props?.onDismiss}>
                    Okay
                </Button>
            ) : null}
        </>
    ) : (
        <Form
            onFinish={values =>
                QuickTurnPartStore.signUpForEmailNotifications({
                    email_address: values.email_address,
                    source: QuickTurnPartStore.emailSignUpSource,
                })
            }
        >
            <Form.Item className="mb-0">
                <Row gutter={[8, 8]} className="mb-0">
                    <Col md={18} span={24}>
                        <Form.Item
                            name="email_address"
                            noStyle
                            rules={[
                                {
                                    type: 'email',
                                    required: true,
                                    message: 'Please enter a valid email address',
                                },
                            ]}
                        >
                            <Input placeholder="Enter your email address" />
                        </Form.Item>
                    </Col>
                    <Col md={6} span={24}>
                        <Button
                            placeholder="Enter your email address"
                            type={props?.submitButtonType ? props?.submitButtonType : 'primary'}
                            htmlType="submit"
                            block
                        >
                            Submit
                        </Button>
                    </Col>
                </Row>
            </Form.Item>
        </Form>
    );
});

interface IMinMaxDimensions {
    min: number;
    max: number;
    unit: 'angle' | 'inch';
}

const MinAndMaxDimensions = (props: IMinMaxDimensions) => {
    return props?.unit === 'inch' ? (
        <div className="d-flex flex-row w-100 justify-content-between">
            <div className="font-size-sm">Min: {props.min.toFixed(3)}"</div>
            <div className="font-size-sm">Max: {props.max.toFixed(3)}"</div>
        </div>
    ) : props.unit === 'angle' ? (
        <div className="d-flex flex-row w-100 justify-content-between">
            <div className="font-size-sm">Min: {props.min.toFixed(0)}°</div>
            <div className="font-size-sm">Max: {props.max.toFixed(0)}°</div>
        </div>
    ) : null;
};

export default observer(PartSpecificationForm);
